<template>
  <section
    v-if="enabled && (!product || product.stock)"
    class="express-checkout"
    :class="{
      'express-checkout--product': !!product,
      'express-checkout--pending': loading,
      'express-checkout--unavailable': !loading && !available && !error,
    }"
    aria-label="Express checkout"
    :aria-busy="busy || loading"
  >
    <h2 v-if="!product" class="express-checkout__title">Express checkout</h2>
    <p v-else class="express-checkout__rule express-checkout__rule--lead">
      <span>or express checkout</span>
    </p>
    <div class="express-checkout__row">
      <div v-if="loading" class="express-checkout__placeholder" role="status">
        <span>Loading express checkout…</span>
      </div>
      <div ref="mountTarget" class="express-checkout__wallets"></div>
    </div>
    <p class="express-checkout__terms">
      By paying, you agree to our
      <a href="/terms" target="_blank" rel="noopener">Terms &amp; Conditions</a
      >. Delivery details come from your wallet.
    </p>
    <p v-if="error" class="express-checkout__error" role="alert">{{ error }}</p>
    <p v-if="!product" class="express-checkout__rule">
      <span>or enter your details below</span>
    </p>
  </section>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { loadStripe } from "@stripe/stripe-js/pure";
import {
  createExpressFlow,
  validateExpressQuote,
} from "../../../utils/express-checkout.mjs";
import { stageExpressPurchase } from "../../../utils/express-tracking.mjs";
import {
  composeGiftOrderComment,
  getGiftOrderPreference,
} from "~/core/utils/giftOrder";
import {
  cartToGa4Ecommerce,
  pushGa4EcommerceEvent,
} from "~/core/utils/ga4Ecommerce";
import { GOOGLE_CUSTOMER_REVIEWS } from "~/core/config/googleCustomerReviews";
import {
  clearPendingReview,
  rememberReviewOrder,
} from "~/core/utils/googleCustomerReviews";

const props = defineProps({
  product: { type: Object, default: null },
  cart: { type: Object, default: null },
});
const emit = defineEmits(["busy"]);
const enabled = import.meta.env.VITE_EXPRESS_CHECKOUT_ENABLED === "true";
const store = useStore();
const mountTarget = ref<HTMLElement | null>(null);
const available = ref(false);
const loading = ref(true);
const busy = ref(false);
const error = ref("");
let wallet: any;
let flow: any;
let revision = 0;
let disposed = false;
// Both pages use 44. On the product page "Add to bag" now carries a 44px
// minimum - the touch-target floor it used to miss at 41.7px - and the wallet
// under it matches edge for edge, so the two ways to buy read as one pair.
// The checkout page already used 44.
const BUTTON_HEIGHT = 44;
// On the product page the wallet is the secondary action: an outlined white
// button with the bare "Pay" mark, so "Add to cart" stays the only solid dark
// button in the column. The checkout page keeps Stripe's default black
// "Buy with" buttons, where paying is the primary action.
const PRODUCT_BUTTONS = {
  buttonTheme: { applePay: "white-outline", googlePay: "white" },
  buttonType: { applePay: "plain", googlePay: "plain" },
};
const CHECKOUT_BUTTONS = {
  buttonType: { applePay: "buy", googlePay: "buy" },
};
// Matches Tailwind's `md` breakpoint, the same one the product column uses.
const TWO_UP = "(min-width: 768px)";
let columns: MediaQueryList | null = null;
// One wallet per row on phones, two side by side from `md` up. maxRows 0 keeps
// every available wallet visible: an overflow menu would bury the one wallet
// the shopper's device actually supports.
const walletLayout = () =>
  props.product
    ? { maxColumns: columns?.matches ? 2 : 1, maxRows: 0, overflow: "never" }
    : { maxColumns: 3, maxRows: 1, overflow: "auto" };
const applyWalletLayout = () => {
  // The element can be torn down between the media change and this callback.
  try {
    wallet?.update({ layout: walletLayout() });
  } catch {
    /* No wallet mounted. */
  }
};
const identity = computed(() =>
  props.product
    ? JSON.stringify([
        props.product.id,
        props.product.stock,
        props.product.price,
        props.product.special,
      ])
    : JSON.stringify([
        props.cart?.total,
        props.cart?.coupon,
        props.cart?.products?.map((item: any) => [
          item.key,
          item.product?.id,
          item.quantity,
          item.product?.price,
          item.product?.special,
        ]),
      ])
);
const consent = () => ({
  analytics: (window as any).vfConsent?.has("analytics") === true,
  marketing: (window as any).vfConsent?.has("marketing") === true,
});
const setBusy = (value: boolean) => {
  busy.value = value;
  emit("busy", value);
};
const destroy = () => {
  flow?.dispose();
  flow = null;
  wallet?.destroy();
  wallet = null;
  available.value = false;
};
const clearPending = () => {
  clearPendingReview();
  try {
    localStorage.removeItem("vf_pending_purchase");
  } catch {
    /* Optional storage. */
  }
};
const initialize = async () => {
  if (
    !enabled ||
    disposed ||
    busy.value ||
    (props.product && !props.product.stock)
  )
    return;
  const current = ++revision;
  destroy();
  loading.value = true;
  error.value = "";
  try {
    const productId = props.product ? String(props.product.id) : undefined;
    const initial = await store.dispatch("store/checkout/order/express", {
      action: "bootstrap",
      productId,
    });
    if (disposed || current !== revision) return;
    if (initial?.status !== "ready") {
      loading.value = false;
      return;
    }
    validateExpressQuote(initial);
    const stripe: any = await loadStripe(initial.publishableKey);
    if (disposed || current !== revision) return;
    if (!stripe) {
      loading.value = false;
      return;
    }
    const elements: any = stripe.elements({
      mode: "payment",
      currency: initial.currency,
      amount: initial.amount,
      appearance: {
        theme: "stripe",
        variables: {
          borderRadius: props.product ? "0px" : "6px",
          fontFamily: "Manrope, Arial, sans-serif",
        },
      },
    });
    wallet = elements.create("expressCheckout", {
      buttonHeight: BUTTON_HEIGHT,
      ...(props.product ? PRODUCT_BUTTONS : CHECKOUT_BUTTONS),
      // Link is an account button, not a device wallet: on the product page it
      // reads as a third brand competing with "Add to bag". It stays on the
      // checkout page, where the shopper has already committed to paying.
      paymentMethodOrder: props.product
        ? ["apple_pay", "google_pay"]
        : ["apple_pay", "google_pay", "link"],
      paymentMethods: {
        applePay: "always",
        googlePay: "auto",
        link: props.product ? "never" : "auto",
        paypal: "never",
        amazonPay: "never",
        klarna: "never",
      },
      layout: walletLayout(),
    });
    const analyticsCart = props.product
      ? { products: [{ product: props.product, quantity: 1 }] }
      : props.cart;
    flow = createExpressFlow({
      initial,
      elements,
      stripe,
      request: (input: any) =>
        store.dispatch("store/checkout/order/express", {
          ...input,
          token: initial.token,
          productId,
        }),
      comment: () =>
        composeGiftOrderComment(
          store.getters["store/checkout/order/details"]?.comment,
          getGiftOrderPreference()
        ),
      onBusy: setBusy,
      onError: (message: string) => {
        error.value = message;
      },
      onBegin: () => {
        if (consent().analytics)
          pushGa4EcommerceEvent("begin_checkout", {
            ...cartToGa4Ecommerce(analyticsCart),
            checkout_type: "express",
          });
      },
      onPrepared: (result: any, event: any) => {
        const allowed = consent();
        // Address/email stay out of GTM and the pending purchase payload.
        try {
          stageExpressPurchase(result, allowed, window.localStorage);
        } catch {
          // Some privacy modes deny access to the storage object itself.
        }
        if (allowed.analytics) {
          pushGa4EcommerceEvent("add_shipping_info", {
            ...result.quote.ecommerce,
            shipping_tier: result.quote.shippingRate,
          });
          pushGa4EcommerceEvent("add_payment_info", {
            ...result.quote.ecommerce,
            payment_type: event.expressPaymentType,
          });
        }
        const name = String(event.billingDetails?.name || "").split(/\s+/, 2);
        void store
          .dispatch("common/klaviyo/identifyCheckout", [
            { name: "email", value: event.billingDetails?.email || "" },
            { name: "firstName", value: name[0] || "" },
            { name: "lastName", value: name[1] || "" },
          ])
          .catch(() => {});
        // Placed Order is sent by the verified paid outbox, even if the tab closes.
        if (GOOGLE_CUSTOMER_REVIEWS.enabled) {
          const country = event.shippingAddress.address.country;
          const delivery =
            country !== "US"
              ? "international"
              : result.quote.shippingRate === "flat.flat"
              ? "expressUs"
              : "standardUs";
          rememberReviewOrder({
            orderId: result.orderId,
            orderNumber:
              new URL(result.returnUrl).searchParams.get("order_number") || "",
            email: event.billingDetails.email,
            country,
            deliveryDays:
              GOOGLE_CUSTOMER_REVIEWS.deliveryBusinessDays[delivery],
            paymentIntentId: result.paymentIntentId,
            stripePublicKey: initial.publishableKey,
            clientSecret: result.clientSecret,
            createdAt: Date.now(),
          });
        }
      },
      onFailed: clearPending,
      navigate: (url: string) => window.location.assign(url),
    });
    wallet.on("ready", (event: any) => {
      if (disposed || current !== revision) return;
      available.value = Object.values(event.availablePaymentMethods || {}).some(
        Boolean
      );
      loading.value = false;
    });
    wallet.on("loaderror", () => {
      if (disposed || current !== revision) return;
      available.value = false;
      loading.value = false;
    });
    wallet.on("click", flow.click);
    wallet.on("shippingaddresschange", flow.shippingAddressChange);
    wallet.on("shippingratechange", flow.shippingRateChange);
    wallet.on("confirm", flow.confirm);
    wallet.on("cancel", () => {
      flow?.cancel();
      if (identity.value !== initializedIdentity) void initialize();
    });
    wallet.mount(mountTarget.value);
    initializedIdentity = identity.value;
  } catch {
    // Older/disabled backend or an unsupported wallet leaves ordinary checkout usable.
    if (disposed || current !== revision) return;
    destroy();
    loading.value = false;
  }
};
let initializedIdentity = "";
watch(identity, () => {
  if (!busy.value) void initialize();
});
onMounted(() => {
  columns = window.matchMedia(TWO_UP);
  columns.addEventListener("change", applyWalletLayout);
  void initialize();
});
onBeforeUnmount(() => {
  disposed = true;
  revision += 1;
  columns?.removeEventListener("change", applyWalletLayout);
  columns = null;
  destroy();
  setBusy(false);
});
</script>

<style scoped>
.express-checkout {
  /* Kept in step with the `buttonHeight` and `borderRadius` handed to Stripe,
     so the loading placeholder occupies exactly the box the wallet will. */
  --ec-wallet-height: 44px;
  --ec-wallet-radius: 6px;
  width: 100%;
  margin-bottom: 24px;
}
/* Product page: the wallet answers "Add to bag", so it borrows that button's
   geometry — 44px tall, square, full column width — and steps back to a
   secondary role instead of competing for the primary action. */
.express-checkout--product {
  --ec-wallet-height: 44px;
  --ec-wallet-radius: 0px;
  margin-bottom: 12px;
}
.express-checkout--unavailable {
  height: 0;
  margin: 0;
  overflow: hidden;
  visibility: hidden;
}
.express-checkout__title {
  margin: 0 0 12px;
  color: #111827;
  font-size: 18px;
  font-weight: 600;
}
.express-checkout__wallets {
  width: 100%;
  min-width: 0;
  min-height: var(--ec-wallet-height);
}
.express-checkout__row {
  position: relative;
  /* min-height, not height: stacked wallets add rows on narrow viewports. */
  min-height: var(--ec-wallet-height);
}
.express-checkout__placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #d8dee8;
  border-radius: var(--ec-wallet-radius);
  background: #f8fafc;
  color: #475467;
  font-size: 12px;
  pointer-events: none;
}
.express-checkout--pending .express-checkout__wallets {
  opacity: 0;
}
.express-checkout__terms {
  margin: 12px 0 0;
  color: #475467;
  font-size: 12px;
  line-height: 1.6;
  text-align: left;
}
.express-checkout--product .express-checkout__terms {
  margin-top: 10px;
}
.express-checkout__terms a {
  color: #111827;
  text-decoration: underline;
  text-underline-offset: 3px;
}
.express-checkout__terms a:focus-visible {
  outline: 2px solid #315ea8;
  outline-offset: 3px;
}
.express-checkout__error {
  margin: 12px 0;
  color: #a71930;
  font-size: 14px;
  line-height: 1.5;
  text-align: left;
}
.express-checkout__rule {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 24px 0 0;
  color: #475467;
  font-size: 13px;
}
/* Leading rule: it labels the wallets below it, so it sits closer to them than
   to the button above. */
.express-checkout__rule--lead {
  margin: 6px 0 12px;
  font-weight: 500;
}
.express-checkout__rule::before,
.express-checkout__rule::after {
  content: "";
  flex: 1;
  height: 1px;
  background: #d8dee8;
}
</style>
